import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, BellRing, Heart } from "lucide-react";
import { HeroV2 } from "@/components/v2/HeroV2";
import { ConstellationIntro, introMode } from "@/components/v2/ConstellationIntro";
import { CategoriesV2 } from "@/components/v2/CategoriesV2";
import { RecentList } from "@/components/RecentList";
import { BrandsExplorer } from "@/components/BrandsExplorer";
import { AboutSection } from "@/components/AboutSection";
import { PopularCarousel } from "@/components/PopularCarousel";
import { FeaturedBrands } from "@/components/FeaturedBrands";
import { useFavourites } from "@/components/Favourites";
import { SectionHead } from "@/components/Reveal";
import { useBrands, usePageTitle } from "@/lib/hooks";

const MoreLink = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-ph-line px-5 py-3 text-sm font-semibold text-ph-ink transition-[background-color,color,border-color] duration-300 hover:border-ph-lime hover:bg-ph-lime hover:text-ph-navy">
    {children} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
  </Link>
);

function FavouriteStrip({ brands }) {
  const { favs } = useFavourites();
  const mine = favs.map((s) => brands.find((b) => b.slug === s)).filter(Boolean);
  if (!mine.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-5 pb-6 md:px-8" data-testid="home-favourites">
      <p className="flex items-center gap-2 font-mono-ph text-xs uppercase tracking-[0.2em] text-ph-muted"><Heart className="h-3.5 w-3.5 fill-rose-500 text-rose-500" /> Your favourite brands</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {mine.map((b) => (
          <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`home-favourite-${b.slug}`} className="rounded-2xl border border-ph-line bg-ph-surface px-4 py-3 text-sm font-semibold text-ph-ink hover:border-ph-accent/50">
            {b.name} <span className="ml-1 text-xs font-normal text-ph-muted">{b.doc_count || 0} lists</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const AlertsCta = () => (
  <section className="mx-auto max-w-7xl px-5 pb-24 md:px-8" data-testid="home-alerts-cta">
    <div className="flex flex-col gap-5 rounded-[2rem] bg-[#0B1020] p-8 text-white md:flex-row md:items-center md:justify-between md:p-10">
      <div>
        <p className="flex items-center gap-2 font-mono-ph text-xs uppercase tracking-[0.2em] text-[#B6FF5C]"><BellRing className="h-4 w-4" /> Follow brands</p>
        <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight">Get an email when a price list changes</h2>
        <p className="mt-2 max-w-xl text-sm text-slate-300">Pick one or many brands. You confirm by email first, and every alert has a one-tap unsubscribe link. No account needed.</p>
      </div>
      <Link to="/brands" data-testid="home-alerts-cta-btn" className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#6366F1] px-6 py-3.5 font-semibold text-white hover:bg-[#5458e8]">Choose brands to follow</Link>
    </div>
  </section>
);

export default function HomePage() {
  usePageTitle("", "Independent directory of electrical-industry price lists. Search a brand, check the effective date, preview and download the PDF or Excel price list.");
  const { brands, loading } = useBrands();
  const [params] = useSearchParams();
  const replay = params.get("intro") === "replay";
  const [introOn, setIntroOn] = useState(() => replay || introMode() !== "none");
  const [play, setPlay] = useState(() => !introOn);
  return (
    <>
      {introOn && (
        <ConstellationIntro force={replay ? 1 : 0} names={brands.map((b) => b.name)} ready={!loading} onExit={() => setPlay(true)} onDone={() => setIntroOn(false)} />
      )}
      <HeroV2 brands={brands} loading={loading} play={play} />
      <FavouriteStrip brands={brands} />
      <section id="recent" className="mx-auto max-w-7xl scroll-mt-28 px-5 py-16 md:px-8 md:py-24">
        <SectionHead eyebrow="Fresh uploads" title="Recently updated price lists" desc="Newly published and replaced price lists, newest upload first." action={<MoreLink to="/recent" testId="home-recent-all">View all updates</MoreLink>} />
        <RecentList limit={6} />
      </section>
      <FeaturedBrands />
      <PopularCarousel />
      <section id="brands" className="bg-ph-surface/60 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead eyebrow="Directory" title="Brand directory" desc="Filter by category, jump by letter, or show only brands with published price lists." action={<MoreLink to="/brands" testId="home-brands-all">Open full directory</MoreLink>} />
          <BrandsExplorer limit={8} />
        </div>
      </section>
      <section id="categories" className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <SectionHead eyebrow="Categories" title="Browse by category" desc="Nine product families covering the electrical supply chain." />
        <CategoriesV2 />
      </section>
      <AlertsCta />
      <section id="about" className="mx-auto max-w-7xl px-5 pb-24 md:px-8">
        <AboutSection />
      </section>
    </>
  );
}
